<template>
  <div
    v-if="toolTip === ''"
    :data-intercom-target="dataTarget"
    class="relative flex items-center justify-center cursor-pointer rounded-[4px] shadow-imp"
    :class="[
      buttonSizeClasses,
      `!${customClass}`,
      variant,
      { disabled: disabled },
    ]"
    @click="onClick"
  >
    <span
      v-if="leadingIcon"
      class="inline-flex items-center justify-center"
      :class="leadingIconClasses"
    >
      <font-awesome-icon :icon="leadingIcon" />
    </span>
    <div :class="[contentClasses, { invisible: loading }]">
      <slot />
    </div>
    <div
      v-if="loading"
      class="absolute rounded-full border-alpha-w50 border-t-white animate-custom-spin"
      :class="loaderClasses"
    ></div>
  </div>
  <!-- kept the tooltip version separate because it breaks tooltips that wraps this component. -->
  <el-tooltip
    v-else
    :content="toolTip"
    :placement="'top'"
    :popper-class="'font-sans'"
    :show-after="500"
  >
    <div
      :data-intercom-target="dataTarget"
      class="relative flex items-center justify-center cursor-pointer rounded-[4px] shadow-imp"
      :class="[
        buttonSizeClasses,
        `!${customClass}`,
        variant,
        { disabled: disabled },
      ]"
      @click="onClick"
      @click.stop
    >
      <span
        v-if="leadingIcon"
        class="inline-flex items-center justify-center"
        :class="leadingIconClasses"
      >
        <font-awesome-icon :icon="leadingIcon" />
      </span>
      <div :class="[contentClasses, { invisible: loading }]">
        <slot />
      </div>
      <div
        v-if="loading"
        class="absolute rounded-full border-alpha-w50 border-t-white animate-custom-spin"
        :class="loaderClasses"
      ></div>
    </div>
  </el-tooltip>
</template>
<script setup lang="ts">
const BUTTON_SIZE_CLASSES = {
  default: {
    button: "h-10 p-[10px] text-sm font-medium",
    content: "",
    icon: "gap-2 px-2",
    loader: "w-5 h-5 border-[3px]",
  },
  "x-small": {
    button: "h-8 p-[6px] gap-[2px]",
    content: "font-sans text-xs not-italic font-semibold",
    icon: "h-5 w-5",
    loader: "w-4 h-4 border-2",
  },
} as const;

const emit = defineEmits(["onClick"]);

const props = defineProps({
  disabled: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  variant: {
    type: String,
    default: "primary",
  },
  size: {
    type: String,
    default: "default",
    validator: (value: string) => ["default", "x-small"].includes(value),
  },
  customClass: {
    type: String,
    default: "",
  },
  toolTip: {
    type: String,
    default: "",
  },
  dataTarget: {
    type: String,
    default: "",
  },
  leadingIcon: {
    type: Array as unknown as () => [string, string], // e.g. ['fas', 'plus']
    default: null,
  },
});

const sizeClasses = computed(
  () => BUTTON_SIZE_CLASSES[props.size as keyof typeof BUTTON_SIZE_CLASSES],
);

const buttonSizeClasses = computed(() => sizeClasses.value.button);
const contentClasses = computed(() => sizeClasses.value.content);
const leadingIconClasses = computed(() => sizeClasses.value.icon);
const loaderClasses = computed(() => sizeClasses.value.loader);

const onClick = () => {
  if (!props.disabled && !props.loading) {
    emit("onClick");
  }
};
</script>
<style scoped>
.primary-strong {
  @apply border-[rgba(17,24,39,0.10)] bg-secondary-700 shadow-primary font-subtitle-2 text-white hover:bg-secondary-800;
}
.primary {
  @apply border-[rgba(17,24,39,0.10)] bg-secondary-700 shadow-primary font-subtitle-2 text-white hover:!bg-secondary-800;
}

.primary-ghost {
  @apply bg-white shadow-none font-subtitle-2 text-secondary-800 hover:bg-secondary-100 focus:border border-secondary-700 disabled:text-slate-400;
}

.primary.disabled {
  @apply bg-accent-100 text-accent-400 hover:!bg-accent-100 cursor-not-allowed;
}

.primary-disabled {
  @apply bg-slate-200 text-slate-400 cursor-not-allowed hover:bg-slate-200;
}

.delete {
  @apply bg-danger-600 text-white;
}

.delete.disabled {
  @apply bg-accent-100 text-accent-400 cursor-not-allowed;
}

.secondary {
  @apply border border-accent-200 bg-white hover:border-accent-200 font-subtitle-2 text-accent-700 hover:bg-accent-100 shadow-primary;
}

.secondary.disabled {
  @apply bg-accent-100 text-accent-400 cursor-not-allowed;
}

.outline {
  @apply bg-white outline-none border border-secondary-700 text-secondary-700 shadow-primary font-body-1 hover:bg-secondary-100 hover:border-secondary-100;
}

.outline-secondary-neutral {
  @apply bg-white outline-none font-medium border border-neutral-200 hover:bg-neutral-100 text-gray-600;
}

.outline-secondary {
  @apply bg-white outline-none font-medium border border-secondary-200 hover:bg-secondary-100 text-secondary-700;
}

.rounded {
  @apply cursor-pointer bg-transparent w-10 h-10 rounded-full m-2 hover:bg-accent-100;
}

.dark {
  @apply bg-primary-700 hover:bg-primary-800;
}

.light {
  @apply border border-accent-200 bg-white hover:bg-gray-100;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.animate-custom-spin {
  animation: spin 2s linear infinite;
}
</style>
